import Box from '@mui/material/Box';
import type { FC } from 'react';
import { useLocation, useParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { mindtimeWazuhVulnerabilitiesApi } from 'src/api/mindtime/wazuhVulnerabilitiesApi';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import { useEdrDetailRecord } from 'src/mindtime/hooks/useEdrDetailRecord';
import type { WazuhVulnerabilityItem } from 'src/types/mindtime/wazuhVulnerabilities';
import { EdrEntityDetailPage } from '../shared/edrPrimitives';
import { getVulnerabilityDetailPreview } from '../preview/vulnerabilities';
import { buildVulnerabilityDetailProps } from './vulnerabilityDetailModel';

type VulnerabilityDetailLocationState = {
  record?: WazuhVulnerabilityItem;
};

const VulnerabilityDetail: FC = () => {
  const { recordId = '' } = useParams<{ recordId: string }>();
  const location = useLocation();
  const vulnerabilityId = decodeURIComponent(recordId).trim();
  const stateRecord = (location.state as VulnerabilityDetailLocationState | null)?.record;

  const { record, loading, notFound } = useEdrDetailRecord({
    moduleId: 'vulnerability-detection',
    recordId: vulnerabilityId,
    initialRecord: stateRecord ?? null,
    getPreview: getVulnerabilityDetailPreview,
    fetchLive: mindtimeWazuhVulnerabilitiesApi.getVulnerability,
    logLabel: 'VulnerabilityDetail',
  });

  const detailProps = record ? buildVulnerabilityDetailProps(record) : null;
  const listHref = getSecurityNavItemPath('vulnerability-detection') ?? '';

  return (
    <>
      <Helmet>
        <title>{getPageTitle(`Security — ${record?.cveId ?? 'Vulnerability'}`)}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{
          gap: 16,
          minHeight: '100%',
          boxSizing: 'border-box',
          background: '#F8FAFC',
        }}
      >
        <EdrEntityDetailPage
          backLink={detailProps?.backLink ?? { label: 'Back to Vulnerabilities', to: listHref }}
          title={detailProps?.title ?? 'Vulnerability'}
          badges={detailProps?.badges}
          metaLine={detailProps?.metaLine}
          main={detailProps?.main ?? null}
          sidebar={detailProps?.sidebar}
          loading={loading}
          notFoundMessage={notFound ? 'Vulnerability record not found.' : undefined}
        />
      </Box>
    </>
  );
};

export default VulnerabilityDetail;
